/**
 * Address component formatting utilities
 *
 * Handles formatting of address components:
 * - Street addresses with suffix abbreviations
 * - City names with proper capitalization
 * - State name/abbreviation normalization
 */

import type { AddressFormattingResult, NameFormattingResult } from './types';
import { normalizeWhitespace, capitalizeWords, isAllCaps } from './text';

/**
 * US street suffix abbreviations (USPS standard)
 */
const STREET_SUFFIXES: Record<string, string> = {
  alley: 'Aly',
  avenue: 'Ave',
  boulevard: 'Blvd',
  circle: 'Cir',
  court: 'Ct',
  drive: 'Dr',
  expressway: 'Expy',
  highway: 'Hwy',
  lane: 'Ln',
  parkway: 'Pkwy',
  place: 'Pl',
  road: 'Rd',
  square: 'Sq',
  street: 'St',
  terrace: 'Ter',
  trail: 'Trl',
  way: 'Way',
};

/**
 * Directional abbreviations (USPS standard)
 */
const DIRECTIONALS: Record<string, string> = {
  north: 'N',
  south: 'S',
  east: 'E',
  west: 'W',
  northeast: 'NE',
  northwest: 'NW',
  southeast: 'SE',
  southwest: 'SW',
};

/**
 * Unit type abbreviations
 */
const UNIT_TYPES: Record<string, string> = {
  apartment: 'Apt',
  apt: 'Apt',
  'apt.': 'Apt',
  suite: 'Ste',
  ste: 'Ste',
  'ste.': 'Ste',
  unit: 'Unit',
  building: 'Bldg',
  floor: 'Fl',
  room: 'Rm',
};

/**
 * US States mapping (full name → abbreviation)
 */
const US_STATES: Record<string, string> = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};

/**
 * Format street address
 * Progressive formatting with intelligent abbreviation
 *
 * @example
 * formatStreetAddress('123 main street')
 * // { formatted: '123 Main Street', standardized: '123 Main St' }
 *
 * formatStreetAddress('456 north elm avenue')
 * // { formatted: '456 North Elm Avenue', standardized: '456 N Elm Ave' }
 */
export function formatStreetAddress(value: string): AddressFormattingResult {
  if (!value) {
    return { formatted: '' };
  }

  // Normalize whitespace
  const normalized = normalizeWhitespace(value).formatted;

  // Special case: PO Box
  if (normalized.toLowerCase().startsWith('po box') || normalized.toLowerCase().startsWith('p.o. box')) {
    const parts = normalized.split(' ');
    const boxNumber = parts.slice(2).join(' ');
    return { formatted: `PO Box ${boxNumber}` };
  }

  // Capitalize words
  const capitalized = capitalizeWords(normalized);
  let formatted = capitalized.formatted;

  // Build standardized version with abbreviations
  const words = formatted.split(' ');
  const standardizedWords = [...words];

  // Abbreviate directionals and street suffixes throughout
  for (let i = 0; i < words.length; i++) {
    const word = words[i].toLowerCase().replace(/\./g, '');

    // Check if it's a directional
    if (DIRECTIONALS[word]) {
      standardizedWords[i] = DIRECTIONALS[word];
    }

    // Check if it's a street suffix (usually last or second-to-last word)
    if (STREET_SUFFIXES[word]) {
      standardizedWords[i] = STREET_SUFFIXES[word];
    }
  }

  // Handle unit types (Apt, Suite, etc.)
  let hasUnitType = false;
  for (let i = 0; i < words.length - 1; i++) {
    const word = words[i].toLowerCase().replace(/\./g, '');
    if (UNIT_TYPES[word]) {
      hasUnitType = true;
      // Found unit type - keep full word in formatted, abbreviate in standardized
      words[i] = UNIT_TYPES[word] === 'Ste' ? 'Suite' : UNIT_TYPES[word];
      standardizedWords[i] = UNIT_TYPES[word];

      // Uppercase the unit number if it contains letters (4b → 4B)
      if (i + 1 < words.length) {
        const unitNumber = words[i + 1];
        if (/[a-zA-Z]/.test(unitNumber)) {
          words[i + 1] = unitNumber.toUpperCase();
          standardizedWords[i + 1] = unitNumber.toUpperCase();
        }
      }

      break;
    }
  }

  formatted = words.join(' ');
  const standardized = standardizedWords.join(' ');

  // Return standardized if different from formatted, or if we processed unit types (even if same)
  return {
    formatted,
    standardized: standardized !== formatted || hasUnitType ? standardized : undefined,
  };
}

/**
 * Format city name with proper capitalization
 * Handles multi-word cities and special cases
 *
 * @example
 * formatCity('san francisco') // { formatted: 'San Francisco' }
 * formatCity('new york city') // { formatted: 'New York City' }
 * formatCity('st. louis') // { formatted: 'St. Louis' }
 */
export function formatCity(value: string): NameFormattingResult {
  if (!value) {
    return { formatted: '' };
  }

  // Normalize whitespace
  const normalized = normalizeWhitespace(value).formatted;

  // Check for all caps
  const warnings: string[] = [];
  if (isAllCaps(normalized)) {
    warnings.push('City name appears to be in all caps - converted to title case');
  }

  // Capitalize words, but handle "d'" specially (like "Coeur d'Alene")
  const words = normalized.split(' ');
  const formattedWords = words.map((word, index) => {
    // Check if word has "d'" pattern
    if (word.toLowerCase().includes("d'") && word.toLowerCase().startsWith("d'")) {
      const parts = word.split("'");
      if (parts.length === 2) {
        // Keep d' lowercase after the first word
        if (index > 0) {
          return "d'" + parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
        } else {
          return "D'" + parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
        }
      }
    }
    return capitalizeWords(word).formatted;
  });

  return {
    formatted: formattedWords.join(' '),
    warnings: warnings.length > 0 ? warnings : undefined,
  };
}

/**
 * Format/normalize state
 * Accepts full name or abbreviation, returns standardized abbreviation
 *
 * @example
 * formatState('california') // { formatted: 'CA' }
 * formatState('ca') // { formatted: 'CA' }
 * formatState('Calif.') // { formatted: 'CA' }
 * formatState('invalid') // { formatted: 'invalid', suggestions: ['Unknown state - please use full state name or 2-letter abbreviation'] }
 */
export function formatState(value: string): AddressFormattingResult {
  if (!value) {
    return { formatted: '' };
  }

  // Normalize and lowercase for comparison
  const normalized = normalizeWhitespace(value).formatted;
  const lower = normalized.toLowerCase().replace(/\./g, '');

  // Check if it's a full state name
  if (US_STATES[lower]) {
    return { formatted: US_STATES[lower] };
  }

  // Check if it's already an abbreviation
  const upper = normalized.toUpperCase().replace(/\./g, '');
  const stateValues = Object.values(US_STATES);
  if (stateValues.includes(upper)) {
    return { formatted: upper };
  }

  // Check for partial matches (e.g., "calif" for California)
  const suggestions: string[] = [];
  for (const [fullName, abbr] of Object.entries(US_STATES)) {
    if (fullName.startsWith(lower) || lower.startsWith(fullName.substring(0, 4))) {
      suggestions.push(`Did you mean ${abbr}?`);
    }
  }

  // If no suggestions, provide generic message
  if (suggestions.length === 0) {
    suggestions.push('Unknown state - please use full state name or 2-letter abbreviation');
  }

  return {
    formatted: normalized,
    suggestions,
  };
}
